/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';

import {takeUntil} from 'rxjs/operators';

import {Destroyable} from '../../../destroyable.component';
import {AnyData, ApiListAnyData, ApiService} from '../../api';
import {isPresent} from '../../utils/lang';
import {escapeRegexp} from '../select/common';

export interface TreeNode<T> {
  text: string;
  value: any;
  data?: T;
  children?: Array<TreeNode<T>>;
  selectable: boolean;
  visible: boolean;
  expanded?: boolean;
  flagImage?: string;
  path?: string;
}

@Component({
  selector: 'gp-tree-select',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TreeSelectComponent),
      multi: true
    }
  ],
  styleUrls: ['./tree-select.component.scss'],
  templateUrl: './tree-select.html'
})
export class TreeSelectComponent extends Destroyable implements ControlValueAccessor, OnChanges, OnInit {

  @Input()
  public parentScrollSelector: string;

  @Input()
  public classes: string[] = [];

  @Input()
  public buttonClasses: any;

  @Input()
  public menuClasses: any;

  @Input()
  public allowClear: boolean = true;

  @Input()
  public placeholder: string;

  @Input()
  public tree: Array<TreeNode<any>>;

  @Input()
  public url: string;

  @Input()
  public responseCallBack: (response: any, lookupMap: Map<any, any>, isDefaultRecipient?: boolean) => Array<TreeNode<any>>;

  @Input()
  public readOnly: boolean = false;

  @Input()
  public lazyValue: string;

  @Input()
  public isSorted: boolean = false;

  @Input()
  public clearAfterSelection = false;

  @Input()
  public isDefaultRecipient = false;

  @Input()
  public expandRoots = true;

  public inputValue: string;

  public loading: boolean = false;
  public open: boolean = false;
  public dropDownLeft: string = '0';
  public triggerSortByTranslationPipe: boolean = true;

  @Output()
  private elementSelected: EventEmitter<any> = new EventEmitter();

  private _value: any;

  public lookupMap: Map<any, any> = new Map<any, any>();

  @ViewChild('filterInput', {static: true})
  private filterInput: ElementRef;

  public get dropdownClasses() {
    return [this.open ? 'open' : '', ...this.classes];
  }

  public onChange = (_: any) => {
    // fake
  }

  public onTouched = () => {
    // fake
  }

  public get value(): any {
    return this._value;
  }

  public set value(value: any) {
    if (value !== this._value) {
      const previous = this._value;
      this._value = value;
      this.onChange(value);
      this.sanitizeTextByRemovingIcons(value);
      this.elementSelected.emit({id: value, previousId: previous, text: value ? this.lookupMap.get(value).text : ''});
      if (this.clearAfterSelection) {
        this._value = undefined;
      }
    }
  }

  // ToDo: remove this function when we can send an icon as input to tree's node
  private sanitizeTextByRemovingIcons(value: any) {
    const currentText = this.lookupMap.get(value)?.text;
    const text = value === '' ? '' : /<i[^>]*>(.*?)<\/i>/.test(currentText)
      ? currentText.replace(/<i[^>]*>(.*?)<\/i>/g, '').trim()
      : currentText;
    this.lookupMap.set(value, {text: text, value: value});
  }

  public get displayValue(): any {
    if (this.lookupMap.has(this.value)) {
      return this.lookupMap.get(this.value).text;
    }

    if (!this.open && isPresent(this.lazyValue) && this.value) {
      return this.lazyValue;
    }
    return (this.value || '').toString();
  }

  @HostListener('document:click', ['$event', '$event.target'])
  public onClickOutside(event: any, targetElement: HTMLElement): void {
    if (targetElement && document.contains(targetElement) && !this.el.nativeElement.contains(targetElement)) {
      if (this.open) {
        this.open = false;
      }
    }
  }

  constructor(@Inject(forwardRef(() => ApiService)) public api: ApiService,
              private changeDetector: ChangeDetectorRef,
              private el: ElementRef,
              private translate: TranslateService) {
    super();
  }

  public ngOnInit() {
    this.translate.onLangChange
      .asObservable()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => this.triggerSortByTranslationPipe = !this.triggerSortByTranslationPipe);
  }

  public ngOnChanges(changes: any) {
    if (changes.tree !== undefined && isPresent(this.tree)) {
      this.lookupMap = new Map<any, any>();
      const visit = (items: Array<TreeNode<any>>) => {
        if (this.isSorted) {
          items.sort((a: TreeNode<any>, b: TreeNode<any>) => {
            return this.translate.instant(a.text) < this.translate.instant(b.text) ? -1 : 1;
          });
        }
        items.forEach((item: TreeNode<any>) => {
          this.lookupMap.set(item.value, item);
          if (isPresent(item.children)) {
            visit(item.children);
          }
        });
      };
      visit(this.tree);
    }
  }

  public writeValue(value: any): void {
    this._value = value;
  }

  public registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public toggleOpen(e: Event = void 0, dropdown) {

    this.inputValue = '';

    if (this.readOnly) {
      return;
    }
    this.open = !this.open;

    setTimeout(() => this.filterInput.nativeElement.focus());

    if (!isPresent(this.tree) && isPresent(this.url)) {
      this.loading = true;
      const tree = [];
      this.api.get(this.url).subscribe(response => {
        if (this.responseCallBack) {
          this.tree = this.responseCallBack(response, this.lookupMap, this.isDefaultRecipient);
        } else {
          const list: ApiListAnyData = response;
          list.data.forEach((item: AnyData) => this.lookupMap.set(item.id, {
            text: item.attributes.name,
            value: item.id < 0 ? -item.id : item.id,
            data: item,
            selectable: true,
            visible: true,
            children: []
          }));

          for (const item of Array.from(this.lookupMap.values())) {
            const parent: TreeNode<AnyData> = this.lookupMap.get(item.data.attributes.parent);
            if (parent !== undefined) {
              parent.children.push(item);
            } else {
              tree.push(item);
            }
          }
          this.tree = tree;
        }

        this.loading = false;
        this.changeDetector.detectChanges();
        this.setDropdownPosition(dropdown);
      });
    }

    this.changeDetector.detectChanges();
    this.setDropDownLeft(dropdown);
    this.setDropdownPosition(dropdown);
  }

  public updatePosition(dropdown): void {
    setTimeout(() => {
      this.setDropdownPosition(dropdown);
    }, 0);
  }

  public removeOption() {
    this.selectFn('');
    this.lazyValue = '';
    this.open = true;
  }

  public isFiltered: Function = (node: TreeNode<any>) => {
    const text = this.translate.instant(node.text);
    const query = new RegExp(escapeRegexp(this.inputValue || ''), 'ig');

    return !(query.test(text) || this.isChildrenExists(node, query));
  }

  public selectFn: Function = (value) => {
    this.value = value;
    this.open = false;
  }

  private isChildrenExists(node: TreeNode<any>, query: RegExp) {
    if (node.children.length) {
      return node.children.find((item: TreeNode<any>) =>
        query.test(this.translate.instant(item.text))
        || this.isChildrenExists(item, query));
    }
  }

  private setDropDownLeft(dropdown) {
    const dropdownRight = dropdown.getBoundingClientRect().right;
    const windowWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    if (windowWidth < dropdownRight) {
      this.dropDownLeft = windowWidth - dropdownRight - 10 + 'px';
      this.changeDetector.markForCheck();
    }
  }

  private setDropdownPosition(dropdown) {
    dropdown.style.visibility = 'hidden';
    const inputRect = this.filterInput.nativeElement.getBoundingClientRect();
    dropdown.style.width = `${inputRect.width}px`;
    dropdown.style.top = '';
    const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    const dropdownRect = dropdown.getBoundingClientRect();
    if (dropdownRect.top + dropdownRect.height > viewportHeight + 50) {
      dropdown.style.top = `${-dropdownRect.height - 30}px`;
    }

    dropdown.style.visibility = '';
    if (this.parentScrollSelector) {
      const parent = dropdown.closest(this.parentScrollSelector);
      if (parent && parent.scrollLeft) {
        dropdown.style.marginLeft = `-${parent.scrollLeft + 12}px`;
      }
    }
  }

}
